import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuCheck, LuCircleAlert, LuCircleX } from 'react-icons/lu';
import { useColorContext } from '../../../context/colorContext';
import BubbleLandingPage from '../buble_landing_page';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function PlynoteVsNotebookLM() {
  const navigate = useNavigate();
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, varkColors } = useColorContext();

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const safeAccent = toSafeHex(activeAccent, '#6366F1');

  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');
  const readingColor = toSafeHex(varkColors?.reading, '#10B981');
  const kinestheticColor = toSafeHex(varkColors?.kinesthetic, '#F59E0B');

  const containerStyle = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
    color: safeTextPrimary,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    padding: '80px 24px 72px',
  };

  const contentStyle = {
    maxWidth: 1120,
    margin: '0 auto',
  };

  const heroStyle = {
    marginBottom: 36,
  };

  const eyebrowStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    borderRadius: 999,
    border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    background: hexToRgba(safeBgSecondary, 0.55, safeBgSecondary),
    color: safeTextSecondary,
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 18,
  };

  const titleStyle = {
    fontSize: 'clamp(2.6rem, 5vw, 4.2rem)',
    lineHeight: 1.02,
    fontWeight: 800,
    marginBottom: 16,
    background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.92, safeAccent)} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const subtitleStyle = {
    maxWidth: 900,
    fontSize: '1.05rem',
    lineHeight: 1.75,
    color: safeTextSecondary,
    marginBottom: 20,
  };

  const cardStyle = {
    marginTop: 24,
    background: hexToRgba(safeBgSecondary, 0.5, safeBgSecondary),
    border: `1px solid ${hexToRgba(safeAccent, 0.14, safeAccent)}`,
    borderRadius: 24,
    padding: '28px 24px',
    boxShadow: `0 18px 60px ${hexToRgba(safeBgPrimary, 0.24, safeBgPrimary)}`,
  };

  const sectionTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: safeTextPrimary,
    marginBottom: 14,
  };

  const sectionDescriptionStyle = {
    marginBottom: 18,
    fontSize: '1rem',
    lineHeight: 1.75,
    color: safeTextSecondary,
  };

  const tableWrapStyle = {
    overflowX: 'auto',
    borderRadius: 20,
    border: `1px solid ${hexToRgba(safeAccent, 0.16, safeAccent)}`,
    background: hexToRgba(safeBgPrimary, 0.32, safeBgPrimary),
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 860,
  };

  const thStyle = {
    textAlign: 'left',
    padding: '14px 16px',
    color: safeTextPrimary,
    fontSize: '0.9rem',
    background: hexToRgba(safeBgSecondary, 0.85, safeBgSecondary),
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.16, safeAccent)}`,
  };

  const tdStyle = {
    padding: '15px 16px',
    color: safeTextSecondary,
    fontSize: '0.95rem',
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.08, safeAccent)}`,
    verticalAlign: 'top',
  };

  const statusChipStyle = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 999,
    color,
    border: `1px solid ${hexToRgba(color, 0.25, color)}`,
    background: hexToRgba(color, 0.08, color),
    fontWeight: 700,
    fontSize: '0.84rem',
    lineHeight: 1,
  });

  const verdictStyle = {
    marginTop: 24,
    padding: '22px 20px',
    borderRadius: 18,
    border: `1px solid ${hexToRgba(safeAccent, 0.18, safeAccent)}`,
    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.72, safeBgSecondary)} 0%, ${hexToRgba(safeAccent, 0.08, safeAccent)} 100%)`,
  };

  const verdictTitleStyle = {
    margin: '0 0 12px',
    color: safeTextPrimary,
    fontSize: '1.15rem',
  };

  const verdictHighlightStyle = {
    color: safeTextPrimary,
    fontWeight: 700,
  };

  const backLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 26,
    color: safeAccent,
    fontWeight: 700,
    textDecoration: 'none',
    fontSize: '0.98rem',
  };

  return (
    <div style={containerStyle}>
      <Helmet>
        <title>Plynote vs NotebookLM — Which AI is better for scientific PDFs?</title>
        <meta name="description" content="Detailed comparison between Plynote and Google NotebookLM. See which AI tool better handles equations, mindmaps, and flashcards for scientific research." />
        <link rel="canonical" href="https://www.plynote.io/comparative/plynote-vs-notebooklm" />
        <meta property="og:title" content="Plynote vs NotebookLM — Which AI is better for scientific PDFs?" />
        <meta property="og:description" content="Detailed comparison between Plynote and Google NotebookLM. See which AI tool better handles equations, mindmaps, and flashcards for scientific research." />
        <meta property="og:url" content="https://www.plynote.io/comparative/plynote-vs-notebooklm" />
        <meta name="twitter:title" content="Plynote vs NotebookLM — Which AI is better for scientific PDFs?" />
        <meta name="twitter:description" content="Detailed comparison between Plynote and Google NotebookLM. See which AI tool better handles equations, mindmaps, and flashcards for scientific research." />
      </Helmet>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

          * {
            box-sizing: border-box;
          }

          body {
            font-family: 'Inter', sans-serif;
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)};
          }

          ::-webkit-scrollbar-thumb {
            background: ${hexToRgba(safeAccent, 0.5, safeAccent)};
            border-radius: 999px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${hexToRgba(safeAccent, 0.72, safeAccent)};
          }
        `}
      </style>

      <BubbleLandingPage
        current="comparative"
        onLogoClick={() => navigate('/')}
        onFeaturesClick={() => navigate('/#features')}
        onPricingClick={() => navigate('/connection')}
        onHelpClick={() => navigate('/#help')}
        onCtaClick={() => navigate('/connection')}
        ctaLabel="Start now"
      />

      <div style={contentStyle}>
        <div style={heroStyle}>
          <div style={eyebrowStyle}>Comparative</div>
          <h1 style={titleStyle}>Plynote vs NotebookLM (Google)</h1>
          <p style={subtitleStyle}>
            NotebookLM is great for text-first research. Plynote is built for scientific documents, equations, audio, and multi-format learning workflows.
            This comparison shows where Plynote goes further for math-heavy and multimodal use cases.
          </p>
        </div>

        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Feature comparison</h2>
          <p style={sectionDescriptionStyle}>
            The table below focuses on the capabilities that matter most for students, researchers, and anyone working with technical documents.
          </p>
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Criterion</th>
                  <th style={thStyle}>Plynote</th>
                  <th style={thStyle}>NotebookLM</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Equation extraction</strong> (PDF, DOCX, PNG, JPEG)</td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(kinestheticColor)}><LuCircleX size={16} /> No</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Formula-aware chat assistant</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(kinestheticColor)}><LuCircleX size={16} /> No</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Deep / Summarized modes</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(kinestheticColor)}><LuCircleX size={16} /> No</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Simultaneous multi-file upload</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Audio support</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                </tr>
                
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Mindmaps</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Audio summaries</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                </tr>
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Flashcards</strong></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                  <td style={tdStyle}><span style={statusChipStyle(readingColor)}><LuCheck size={16} /> Yes</span></td>
                </tr>
                
                <tr>
                  <td style={tdStyle}><strong style={{ color: safeTextPrimary }}>Monthly price</strong></td>
                  <td style={tdStyle}>$8.08</td>
                  <td style={tdStyle}>Free / $15 / $500</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section style={verdictStyle}>
          <h3 style={verdictTitleStyle}>Verdict</h3>
          <p style={{ ...subtitleStyle, marginBottom: 0 }}>
            <span style={verdictHighlightStyle}>NotebookLM is excellent for text-first research.</span> But for exact sciences, its inability to process equations makes it less suitable.
            Plynote is built to understand formulas, handle multimodal sources, and stay competitive on pricing.
          </p>
        </section>

      </div>
    </div>
  );
}
